import React, { useEffect, useState } from "react";
import { Form, Modal, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { errorHandling } from "../../config/AuthIntercept";
import {
  GetEsidPreAwbExpCusRefByEsidId,
  CreateEsidPreAwbExpCusRef,
  UpdateEsidPreAwbExpCusRef,
  DeleteEsidPreAwbExpCusRef,
  ImportExcelEsidPreAwbExpCusRef,
} from "../../config/esidPreAwbExpCusRefApi";
import { DeclareCustomsTable } from "./DeclareCustomsTable";
import { CustomsDeclModal } from "./CustomsDeclModal";

export const DeclareCustomsContainer = ({ isEsidExpFinalized }) => {
  const { t } = useTranslation("common");
  const history = useHistory();
  const location = useLocation();
  const esidSelected = useSelector((state) => state.esidExport.esidExpSelected);

  const [customsDeclList, setCustomsDeclList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchCustomsDeclList = async () => {
    if (!esidSelected?.id) {
      setCustomsDeclList([]);
      return;
    }
    try {
      setListLoading(true);
      const { data } = await GetEsidPreAwbExpCusRefByEsidId(esidSelected.id);
      setCustomsDeclList(data || []);
    } catch (error) {
      setCustomsDeclList([]);
      errorHandling(error, history, location.pathname, false);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomsDeclList();
    // eslint-disable-next-line
  }, [esidSelected?.id]);

  const handleAddOrSave = async (formValue, editKey = "") => {
    const payload = {
      esidId: esidSelected.id,
      codAwbPfx: esidSelected.codAwbPfx,
      codAwbNum: esidSelected.codAwbNum,
      codAwbSfx: esidSelected.codAwbSfx,
      codCusRefNum: formValue.codCusRefNum,
      datCusIss: formValue.datCusIss,
      acpDat: esidSelected.acpDat,
      acpStfCod: esidSelected.acpStfCod,
      acpStfNam: esidSelected.acpStfNam,
      codSts: esidSelected.codSts,
      desRmk: esidSelected.desRmk,
    };
    try {
      setSaving(true);
      if (editKey) {
        await UpdateEsidPreAwbExpCusRef(editKey, { ...payload, usrUpd: esidSelected.usrCre });
      } else {
        await CreateEsidPreAwbExpCusRef({ ...payload, usrCre: esidSelected.usrCre });
      }
      notification.success({
        message: t("Congrats"),
        description: t("admin.notification.saveSuccess"),
      });
      setModalVisible(false);
      setEditingKey("");
      form.resetFields();
      await fetchCustomsDeclList();
    } catch (error) {
      errorHandling(error, history, location.pathname, true);
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (file) => {
    try {
      setImporting(true);
      await ImportExcelEsidPreAwbExpCusRef(esidSelected.id, file);
      notification.success({
        message: t("Congrats"),
        description: t("admin.notification.saveSuccess"),
      });
      await fetchCustomsDeclList();
    } catch (error) {
      errorHandling(error, history, location.pathname, true);
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = () => {
    setEditingKey("");
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingKey(record.id);
    form.setFields([
      { name: "codCusRefNum", value: record.codCusRefNum },
      { name: "datCusIss", value: record.datCusIss ? moment(record.datCusIss) : undefined },
    ]);
    setModalVisible(true);
  };

  const handleClearEdit = () => {
    setEditingKey("");
    form.resetFields();
  };

  const showDeleteConfirm = (record) => {
    Modal.confirm({
      title: t("dangerousGood.tableInfo.deleteConfirm"),
      onOk: async () => {
        try {
          await DeleteEsidPreAwbExpCusRef(record.id);
          await fetchCustomsDeclList();
        } catch (error) {
          errorHandling(error, history, location.pathname, true);
        }
      },
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      centered: true,
    });
  };

  return (
    <>
      {modalVisible && (
        <CustomsDeclModal
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          editingKey={editingKey}
          form={form}
          handleAddOrSave={handleAddOrSave}
          handleClearEdit={handleClearEdit}
          saving={saving}
        />
      )}
      <DeclareCustomsTable
        dataSource={customsDeclList}
        handleEdit={handleEdit}
        handleDelete={showDeleteConfirm}
        handleAdd={handleAdd}
        handleImport={handleImport}
        importing={importing}
        isEsidExpFinalized={isEsidExpFinalized}
        loading={listLoading}
      />
    </>
  );
};
